import { motion } from "framer-motion";
import {
  TrendingDown,
  Zap,
  Users,
  Shield,
  BarChart3,
  Gauge,
  Globe,
  HeadphonesIcon,
} from "lucide-react";

const valueProps = [
  {
    icon: TrendingDown,
    title: "60% Cost Savings",
    description:
      "In-house agents cost $15-20/hr with benefits, taxes, and overhead. Pragma delivers at $8/hr all-inclusive.",
    metric: "vs In-House",
    color: "cyan",
  },
  {
    icon: Zap,
    title: "14-Day Deployment",
    description:
      "Traditional hiring takes 4-8 weeks. We deploy fully trained, professional teams in just 14 business days.",
    metric: "vs 4-8 Weeks",
    color: "magenta",
  },
  {
    icon: Users,
    title: "Zero HR Burden",
    description:
      "We handle recruitment, payroll, taxes, benefits, compliance, training, and performance management.",
    metric: "Fully Managed",
    color: "cyan",
  },
  {
    icon: Shield,
    title: "Quality Guaranteed",
    description:
      "Real-time monitoring, continuous training, and rigorous QA ensure professional interactions every time.",
    metric: "Enterprise Grade",
    color: "magenta",
  },
  {
    icon: Gauge,
    title: "Flexible Scaling",
    description:
      "Scale your team up or down instantly. No long-term contracts. Pay only for the hours you need.",
    metric: "No Contracts",
    color: "cyan",
  },
  {
    icon: Globe,
    title: "Multi-Channel Support",
    description:
      "Email, live chat, WhatsApp, phone, video calls—we cover all channels your customers prefer.",
    metric: "All Channels",
    color: "magenta",
  },
  {
    icon: BarChart3,
    title: "Advanced Analytics",
    description:
      "Real-time dashboards, performance metrics, weekly reports, and actionable insights included.",
    metric: "Data-Driven",
    color: "cyan",
  },
  {
    icon: HeadphonesIcon,
    title: "24/7 Coverage",
    description:
      "Round-the-clock support across time zones with built-in redundancy and backup agents.",
    metric: "Always On",
    color: "magenta",
  },
];

export default function ValueProps() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section id="features" className="py-24 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 border border-cyan-400/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 border border-pink-500/5 rounded-full translate-x-1/2 translate-y-1/2" />

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-block px-4 py-1.5 bg-cyan-400/10 border border-cyan-400/30 text-cyan-400 text-sm font-mono rounded-full mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            Why Choose Us
          </motion.span>
          <h2 className="text-foreground mb-6">
            Why Choose <span className="text-cyan-400">Pragma?</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Enterprise-grade customer support at a fraction of the cost. We
            handle everything—you focus on growing your business.
          </p>
        </motion.div>

        {/* Value Props Grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {valueProps.map((prop, index) => {
            const Icon = prop.icon;
            const isCyan = prop.color === "cyan";

            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`group relative p-6 border transition-all duration-300 bg-card/50 backdrop-blur-sm ${
                  isCyan ? "neon-border-cyan" : "neon-border-magenta"
                } ${
                  isCyan ? "card-glow-cyan" : "card-glow-magenta"
                }`}
                whileHover={{ y: -8 }}
                data-testid={`value-prop-${index}`}
              >
                {/* Corner Accent */}
                <div
                  className={`absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 ${
                    isCyan ? "border-cyan-400" : "border-pink-500"
                  }`}
                />

                {/* Icon */}
                <div
                  className={`mb-4 w-12 h-12 flex items-center justify-center border ${
                    isCyan
                      ? "border-cyan-400/50 text-cyan-400"
                      : "border-pink-500/50 text-pink-500"
                  }`}
                >
                  <Icon className="w-6 h-6" />
                </div>

                {/* Content */}
                <h3
                  className={`font-mono font-bold text-lg mb-2 transition-colors ${
                    isCyan
                      ? "text-foreground group-hover:text-cyan-400"
                      : "text-foreground group-hover:text-pink-500"
                  }`}
                >
                  {prop.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  {prop.description}
                </p>

                {/* Metric Badge */}
                <div
                  className={`inline-block px-3 py-1 text-xs font-mono font-bold uppercase tracking-wider border ${
                    isCyan
                      ? "border-cyan-400/50 text-cyan-400"
                      : "border-pink-500/50 text-pink-500"
                  }`}
                >
                  {prop.metric}
                </div>

                {/* Hover Glow Overlay */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity pointer-events-none ${
                    isCyan
                      ? "bg-gradient-to-br from-cyan-400 to-transparent"
                      : "bg-gradient-to-br from-pink-500 to-transparent"
                  }`}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
